import ImageLogo from "@/assets/images/logo.png";
import LoginVideo from "@/assets/video/login-background.mp4";
import ErrorBoundary from "@/components/ErrorBoundary/errorBoundary.component";
import Login from "@/components/Login/login.component";
import MagicLink from "@/components/Login/magicLink.component";
import Register from "@/components/Register/registrationForm.component";
import { Button } from "@/shadcn/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shadcn/components/ui/card";
import { Switch } from "@/shadcn/components/ui/switch";
import { utilClearLoginLocalStorage } from "@/utils/tokens.util";
import { getBuildDate } from "@/utils/utils.util";
import { LockIcon } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

type PageLoginProps = {
  // TODO: add props here
};

const PageLogin: React.FC<PageLoginProps> = (props) => {
  const { t } = useTranslation();
  const projectName = import.meta.env.VITE_PROJECT_DISPLAY_NAME;
  utilClearLoginLocalStorage();
  const buildDate = getBuildDate();
  const [section, setSection] = useState<"login" | "register">("login");
  const [loginMethod, setLoginMethod] = useState<"email" | "magicLink">(
    "magicLink"
  );

  const [email, setEmail] = useState<string>("");

  function lowerCaseEmail(newEmail: string) {
    setEmail(newEmail.toLowerCase().trim());
  }

  return (
    <div className="bg-neutral-900">
      <video
        src={LoginVideo}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="fixed top-0 left-0 z-0 h-full w-full object-cover opacity-30"
      ></video>
      <div className="fixed top-2 left-2 flex h-24 items-center">
        <img src={ImageLogo} alt="Logo" className="h-full object-contain" />
        {projectName && (
          <div className="ml-4 text-2xl font-bold text-white sm:text-4xl md:text-5xl">
            {projectName}
          </div>
        )}
      </div>
      <div className="flex h-svh items-center justify-center bg-neutral-900 align-middle">
        <Card
          className={`relative m-auto w-full rounded-sm border-none p-3 px-6 shadow-lg md:px-12 ${section === "login" ? "max-w-lg" : "max-w-2xl px-0 md:px-0"}`}
        >
          <CardHeader>
            <CardTitle className="text-center text-2xl font-bold">
              {section === "login" ? t("Login") : t("Register")}
            </CardTitle>
          </CardHeader>
          <CardContent
            className="m-auto max-h-[80vh] w-full overflow-y-auto text-center"
            style={{ scrollbarWidth: "none" }}
          >
            {section === "login" ? (
              <>
                <div className="mb-4 flex items-center justify-center gap-2">
                  {loginMethod === "email" && (
                    <span className="text-sm text-neutral-400">
                      {t("Password Login")}
                    </span>
                  )}
                  <Switch
                    className="cursor-pointer"
                    checked={loginMethod === "magicLink"}
                    onCheckedChange={(checked) =>
                      setLoginMethod(checked ? "magicLink" : "email")
                    }
                    id="login-method-switch"
                  />
                  {loginMethod === "magicLink" && (
                    <span className="text-sm text-neutral-400">
                      {t("Magic Link")}
                    </span>
                  )}
                </div>
                {loginMethod === "email" ? (
                  <ErrorBoundary key="login">
                    <Login
                      email={email}
                      setEmail={lowerCaseEmail}
                      goToMagicLink={() => setLoginMethod("magicLink")}
                    />
                  </ErrorBoundary>
                ) : (
                  <ErrorBoundary key="magicLink">
                    <MagicLink email={email} setEmail={lowerCaseEmail} />
                  </ErrorBoundary>
                )}
              </>
            ) : (
              <ErrorBoundary key="register">
                <Register />
              </ErrorBoundary>
            )}
            <div className="mt-1">
              {section === "login" ? (
                <div>
                  {t("Don't have an account?")}
                  <Button
                    variant={"link"}
                    className="p-1 font-bold"
                    onClick={() => setSection("register")}
                  >
                    {t("Register")}
                  </Button>
                </div>
              ) : (
                <div>
                  {t("Already have an account?")}
                  <Button
                    variant={"link"}
                    className="p-1 font-bold"
                    onClick={() => setSection("login")}
                  >
                    {t("Login")}
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-2 flex items-center justify-center opacity-70">
              <LockIcon className="mr-2 h-4" />
              <p>{t("Enhanced Security Activated")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
      {buildDate && (
        <div className="absolute right-1 bottom-1 text-black opacity-30">
          {`Build Date: ${buildDate.toLocaleDateString()} ${buildDate.toLocaleTimeString()}`}
        </div>
      )}
    </div>
  );
};

export default PageLogin;
